import React from 'react';
import { Box } from '../box';
import { ownerDocument, ownerWindow } from '../../utils';
import { useEventCallback, useTheme } from '../../hooks';
import { LikeIcon } from '../../icons';
import { mergeSx } from '../../utils/sx/merge';
import { AssessmentState, DrawerSwipeInstance, SwipeableCarouselWithAssessmentProps, TranslateRange } from './types';
import { calculateCurrentX, calculateCurrentY, getTranslate } from './lib';
import { SELECTION_ENTERING_THRESHOLD_RATE, UNCERTAINTY_THRESHOLD } from './const';
import { Instruction } from './components/instruction';

export * from './types';

let claimedSwipeInstance: DrawerSwipeInstance | null | true = null;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const SwipeableCarouselWithAssessment = <ItemData extends unknown>({
  itemsData,
  itemComponent: Item,
  itemContainerProps,
  swipingDisabled = false,
  showInstruction,
  onLike,
  onDislike,
  onInstructionCompleted,
}: SwipeableCarouselWithAssessmentProps<ItemData>) => {
  const theme = useTheme();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const paperRef = React.useRef<HTMLDivElement>(null);
  const touchDetected = React.useRef(false);
  const swipeInstance = React.useRef<DrawerSwipeInstance>({
    startX: 0,
    startY: 0,
    scrollStartX: 0,
    scrollStartY: 0,
    velocity: 0,
    lastTime: null,
    lastTranslate: null,
    baseTranslate: 0,
    paperHit: false,
    isSwiping: null,
  });
  const isTransitionActive = React.useRef(false);
  const resetDrawerPositionOnItemIndex = React.useRef<number | undefined>(0);
  const [itemIndex, setItemIndex] = React.useState(0);
  const [assessmentState, setAssessmentState] = React.useState(AssessmentState.None);

  const nextItemIndex = itemIndex + 1;
  const isNextItemExists = itemsData.length >= nextItemIndex + 1;

  const transitionDuration = React.useMemo(
    () => ({
      enter: theme.transitions.duration.enteringScreen,
      exit: theme.transitions.duration.leavingScreen,
    }),
    [theme.transitions.duration.enteringScreen, theme.transitions.duration.leavingScreen],
  );

  const setPosition = React.useCallback(
    (translate: number, translateRange: TranslateRange, options: { mode?: 'enter' | 'exit'; onComplete?: () => void } = {}) => {
      if (!paperRef.current || isTransitionActive.current) {
        return;
      }

      const { mode = null } = options;

      const transform = `translate(${translate}px, 0)`;
      const drawerStyle = paperRef.current.style;

      if (mode) {
        const transition = theme.transitions.create('all', {
          duration: transitionDuration[mode],
        });

        drawerStyle.webkitTransition = transition;
        drawerStyle.transition = transition;

        isTransitionActive.current = true;
        setTimeout(() => {
          drawerStyle.webkitTransition = '';
          drawerStyle.transition = '';

          isTransitionActive.current = false;

          options.onComplete?.();
        }, transitionDuration[mode]);
      }

      drawerStyle.webkitTransform = transform;
      drawerStyle.transform = transform;
    },
    [theme.transitions, transitionDuration],
  );

  const getTranslateRange = React.useCallback((): TranslateRange => {
    const range = {
      min: 0,
      max: 0,
    };

    if (!paperRef.current) {
      return range;
    }

    const drawerWidth = paperRef.current.offsetWidth;

    range.min = drawerWidth * -1;
    range.max = drawerWidth;

    return range;
  }, []);

  const getBaseTranslate = React.useCallback(({}: { translateRange: TranslateRange }) => {
    return 0;
  }, []);

  const updateAssessmentState = React.useCallback(() => {
    setAssessmentState(
      swipeInstance.current.isSwiping && typeof swipeInstance.current.lastTranslate === 'number'
        ? swipeInstance.current.lastTranslate > 0
          ? AssessmentState.LikeInProgress
          : AssessmentState.DislikeInProgress
        : AssessmentState.None,
    );
  }, []);

  React.useEffect(() => {
    if (resetDrawerPositionOnItemIndex.current !== itemIndex) {
      return;
    }

    const translateRange = getTranslateRange();
    const baseTranslate = getBaseTranslate({ translateRange });

    setPosition(baseTranslate, translateRange, {
      mode: !itemIndex ? 'exit' : undefined,
    });

    resetDrawerPositionOnItemIndex.current = undefined;
  }, [getBaseTranslate, getTranslateRange, setPosition, itemIndex]);

  const handleBodyTouchStart = useEventCallback((nativeEvent: TouchEvent & { defaultPreventedCustom?: boolean }) => {
    if (swipingDisabled) {
      return;
    }

    if (isTransitionActive.current) {
      return;
    }

    // We are not supposed to handle this touch move.
    // Example of use case: ignore the event if there is a Slider.
    if (nativeEvent.defaultPrevented) {
      return;
    }

    // We can only have one node at the time claiming ownership for handling the swipe.
    if (nativeEvent.defaultPreventedCustom) {
      return;
    }

    // At least one element clogs the drawer interaction zone.
    if (!paperRef.current?.contains(nativeEvent.target as Node)) {
      return;
    }

    const currentX = calculateCurrentX(nativeEvent.touches);
    const currentY = calculateCurrentY(nativeEvent.touches, ownerWindow(nativeEvent.currentTarget as Node));

    nativeEvent.defaultPreventedCustom = true;
    claimedSwipeInstance = null;
    swipeInstance.current.startX = currentX;
    swipeInstance.current.startY = currentY;

    swipeInstance.current.scrollStartY = 0;

    const translateRange = getTranslateRange();
    swipeInstance.current.baseTranslate = getBaseTranslate({ translateRange });

    swipeInstance.current.velocity = 0;
    swipeInstance.current.lastTime = null;
    swipeInstance.current.lastTranslate = null;
    swipeInstance.current.paperHit = false;

    touchDetected.current = true;
  });

  const handleBodyTouchEnd = useEventCallback((nativeEvent: TouchEvent) => {
    if (!paperRef.current) {
      return;
    }
    if (!touchDetected.current) {
      return;
    }
    claimedSwipeInstance = null;
    touchDetected.current = false;

    // The swipe wasn't started.
    if (!swipeInstance.current.isSwiping) {
      swipeInstance.current.isSwiping = null;
      updateAssessmentState();
      return;
    }

    swipeInstance.current.isSwiping = null;

    const currentX = nativeEvent.touches.length
      ? calculateCurrentX(nativeEvent.touches)
      : swipeInstance.current.startX + (swipeInstance.current.lastTranslate ?? 0);

    const startLocation = swipeInstance.current.startX;
    const translateRange = getTranslateRange();
    const currentTranslate = getTranslate({
      baseTranslate: swipeInstance.current.baseTranslate,
      currentX,
      startLocation,
      translateRange,
    });
    const baseTranslate = getBaseTranslate({ translateRange });

    const drawerWidth = paperRef.current.offsetWidth;

    const selectionActivationThreshold = SELECTION_ENTERING_THRESHOLD_RATE * drawerWidth;

    if (Math.abs(currentTranslate) > selectionActivationThreshold) {
      setPosition(currentTranslate > 0 ? drawerWidth : -drawerWidth, translateRange, {
        mode: 'exit',
        onComplete: () => {
          try {
            const parameters = { data: itemsData[itemIndex], index: itemIndex };
            if (currentTranslate > 0) {
              onLike?.(parameters);
            } else {
              onDislike?.(parameters);
            }
          } catch (err) {
            throw err;
          } finally {
            updateAssessmentState();

            if (isNextItemExists) {
              setItemIndex((currentItemIndex) => {
                const _nextItemIndex = currentItemIndex + 1;
                resetDrawerPositionOnItemIndex.current = _nextItemIndex;
                return _nextItemIndex;
              });
            }
          }
        },
      });
    } else {
      setPosition(baseTranslate, translateRange, {
        mode: 'exit',
        onComplete: updateAssessmentState,
      });
    }
  });

  const handleBodyTouchMove = useEventCallback((nativeEvent: TouchEvent) => {
    // the ref may be null when a parent component updates while swiping
    if (!paperRef.current || !touchDetected.current) {
      return;
    }

    // We are not supposed to handle this touch move because the swipe was started in a scrollable container in the drawer
    if (claimedSwipeInstance !== null && claimedSwipeInstance !== swipeInstance.current) {
      return;
    }

    const currentX = calculateCurrentX(nativeEvent.touches);
    const currentY = calculateCurrentY(nativeEvent.touches, ownerWindow(nativeEvent.currentTarget as Node));

    // We don't know yet.
    if (swipeInstance.current.isSwiping === null) {
      const dx = Math.abs(currentX - swipeInstance.current.startX);
      const dy = Math.abs(currentY - swipeInstance.current.startY);

      const definitelySwiping = dx > dy && dx > UNCERTAINTY_THRESHOLD;

      if (definitelySwiping || dy > UNCERTAINTY_THRESHOLD) {
        swipeInstance.current.isSwiping = definitelySwiping;
        if (!definitelySwiping) {
          handleBodyTouchEnd?.(nativeEvent);
          return;
        }

        // Shift the starting point.
        swipeInstance.current.startX = currentX;
        swipeInstance.current.startY = currentY;
      }
    }

    if (!swipeInstance.current.isSwiping) {
      return;
    }

    const translateRange = getTranslateRange();
    const startLocation = swipeInstance.current.startX;

    const translate = getTranslate({
      baseTranslate: swipeInstance.current.baseTranslate,
      currentX,
      startLocation,
      translateRange,
    });

    if (swipeInstance.current.lastTranslate === null) {
      swipeInstance.current.lastTranslate = translate;
      swipeInstance.current.lastTime = performance.now() + 1;
    }

    const velocity =
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      ((translate - swipeInstance.current.lastTranslate) / (performance.now() - Number(swipeInstance.current.lastTime))) * 1e3;

    // Low Pass filter.
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    swipeInstance.current.velocity = swipeInstance.current.velocity * 0.4 + velocity * 0.6;

    swipeInstance.current.lastTranslate = translate;
    swipeInstance.current.lastTime = performance.now();

    // We are swiping, let's prevent the scroll event on iOS.
    // if (nativeEvent.cancelable) {
    //   nativeEvent.preventDefault();
    // }

    updateAssessmentState();
    setPosition(translate, translateRange);
  });

  React.useEffect(() => {
    if (handleBodyTouchStart && handleBodyTouchMove && handleBodyTouchEnd) {
      const doc = ownerDocument(paperRef.current);
      doc.addEventListener('touchstart', handleBodyTouchStart);
      // A blocking listener prevents Firefox's navbar to auto-hide on scroll.
      // It only needs to prevent scrolling on the drawer's content when open.
      // When closed, the overlay prevents scrolling.
      doc.addEventListener('touchmove', handleBodyTouchMove, { passive: true });
      doc.addEventListener('touchend', handleBodyTouchEnd);

      return () => {
        doc.removeEventListener('touchstart', handleBodyTouchStart);
        doc.removeEventListener('touchmove', handleBodyTouchMove, { passive: true } as EventListenerOptions);
        doc.removeEventListener('touchend', handleBodyTouchEnd);
      };
    }
  }, [handleBodyTouchEnd, handleBodyTouchMove, handleBodyTouchStart]);

  const accentColor =
    assessmentState === AssessmentState.None
      ? 'transparent'
      : assessmentState === AssessmentState.LikeInProgress
        ? theme.palette.green
        : theme.palette['brand pink hover'];

  return (
    <Box ref={containerRef} sx={{ position: 'relative', overflow: 'hidden' }}>
      {isNextItemExists && (
        <Box
          {...itemContainerProps}
          sx={mergeSx(
            {
              backgroundColor: theme.palette.background.paper,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              border: '4px solid transparent',
            },
            itemContainerProps?.sx,
          )}
        >
          <Item data={itemsData[nextItemIndex]} index={nextItemIndex} isOnTop={false} />
        </Box>
      )}
      <Box
        {...itemContainerProps}
        // TODO: merge refs
        ref={paperRef}
        sx={mergeSx(
          {
            backgroundColor: theme.palette.background.paper,
            position: 'relative',
            zIndex: 2,
            transition: theme.transitions.create('border-color'),
            border: `4px solid ${accentColor}`,
          },
          itemContainerProps?.sx,
        )}
      >
        <Item data={itemsData[itemIndex]} index={itemIndex} isOnTop />

        {assessmentState !== AssessmentState.None && (
          <Box
            sx={{
              position: 'absolute',
              zIndex: 3,
              width: 48,
              height: 48,
              borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: '1px 2px 4px 0px rgba(0, 0, 0, 0.11) inset',
              backgroundColor: accentColor,
              color: theme.palette.white,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              ...(assessmentState === AssessmentState.LikeInProgress ? { top: 16, left: 16 } : { top: 16, right: 16 }),
            }}
          >
            <LikeIcon
              fontSize="2rem"
              sx={assessmentState === AssessmentState.DislikeInProgress ? { transform: 'rotate(180deg)' } : undefined}
            />
          </Box>
        )}
      </Box>

      {showInstruction && <Instruction onCompleted={onInstructionCompleted} />}
    </Box>
  );
};
